import { createSelector } from 'reselect';
import { ARTICLE, CATEGORY } from './constants';
// Reducer
import { checkInArray, initialState } from './reducer';

/**
 * Direct selector to the Faq state domain
 */

const selectFaqDomain = (state) => state.get('faq', initialState);

const getPageId = (state) => selectFaqDomain(state).get('pageId');

const getCategoryName = (state) => selectFaqDomain(state).get('categoryName');

/**
 * Other specific selectors
 */

/**
 * @description Selector that return the page content (Article|Category|Home) based on pageId
 * @param categories: categories from Faq data
 * @param articles: articles from Faq data
 * @return {Object}
 */
const selectFaqPage = (categories, articles) =>
  createSelector(
    selectCategoryPage(categories),
    selectArticlePage(articles),
    (categoryPage, articlePage) => {
      if (categoryPage) return { type: CATEGORY, content: categoryPage };
      if (articlePage) return { type: ARTICLE, content: articlePage };
      return { type: null };
    },
  );

/**
 * @description Selector that check if pageId name is a category
 * If it does: return the category content
 * If not: return null
 * @param categories: categories from Faq data
 * @return {Object|null}
 */
const selectCategoryPage = (categories) =>
  createSelector(getPageId, (pageId) => checkInArray(categories, pageId));

/**
 * @description Selector that check if pageId name is an article
 * If it does: return the article content
 * If not: return null
 * @param articles: articles from Faq data
 * @return {Object|null}
 */
const selectArticlePage = (articles) =>
  createSelector(getPageId, (pageId) => checkInArray(articles, pageId));

/**
 * @description Selector that return the list of articles
 * that are related to a specific category based on the pageId name
 * @param articles: articles from Faq data
 * @return {Array}
 */
const selectArticlesList = (articles) =>
  createSelector(getPageId, (pageId) => {
    const relatedCategory = (item) => item === pageId;
    const articlesList = articles.filter((article) =>
      article.category.some(relatedCategory),
    );
    return articlesList;
  });

/**
 * @description Selector that return the categoryName associed with the current page
 * If we are on a CategoryPage: return the categoryPage name
 * If we are on an ArticlePage:
 *  - If the previous page was a category and
 *    this category is a relatedCategory of
 *    the current article: return the categoryName in the store
 *  - If the previous page was null or it's not a relatedCategory of
 *    the current article: return the first relatedCategory name present in the article content
 * If we are not on CategoryPage or on ArticlePage: return null
 * @param categories: categories from Faq data
 * @param articles: articles from Faq data
 * @return {Object|null}
 */
const selectCategoryName = (categories, articles) =>
  createSelector(
    getCategoryName,
    selectCategoryPage(categories),
    selectArticlePage(articles),
    (categoryName, categoryPage, articlePage) => {
      if (categoryPage) return categoryPage.name;
      if (articlePage) {
        if (categoryName && articlePage.category.includes(categoryName))
          return categoryName;
        return articlePage.category[0];
      }
      return null;
    },
  );

/**
 * @description Selector that fetch the related questions based on current article
 * @param articles: articles from Faq data
 * @return {Array}
 */
const selectRelatedQuestions = (articles) =>
  createSelector(selectArticlePage(articles), (articlePage) => {
    if (articlePage && articlePage.relatedQuestions) {
      const relatedQuestions = articles.reduce((total, value) => {
        if (articlePage.relatedQuestions.includes(value.name))
          return total.concat(value);
        return total;
      }, []);
      return relatedQuestions;
    }
    return [];
  });

/**
 * Default selector used by Faq
 */

const makeSelectFaq = () =>
  createSelector(selectFaqDomain, (substate) => substate.toJS());

export default makeSelectFaq;
export {
  selectArticlesList,
  selectCategoryName,
  selectFaqDomain,
  selectFaqPage,
  selectRelatedQuestions,
};
